import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Activity } from "lucide-react";

const usd = (n) => `$${Number(n || 0).toFixed(4)}`;
const ROUTE = { network: "served by a shared credential", shared_out: "served for the network", cache: "reused result" };

function Table({ title, rows, testId }) {
    return (
        <div className="rounded border border-white/10 bg-[#050505] p-3" data-testid={testId}>
            <div className="label-xs mb-2">{title}</div>
            {(!rows || rows.length === 0) && <div className="text-[11px] text-white/40">No activity yet.</div>}
            {rows?.map((r) => (
                <div key={r.key} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] py-0.5 border-t border-white/5 first:border-t-0" data-testid={`${testId}-row-${r.key}`}>
                    <span className="text-white/85 min-w-[9rem]">{ROUTE[r.key] || r.key}</span>
                    <span className="font-mono text-white/70">{r.calls} calls</span>
                    <span className="font-mono text-white/70">{usd(r.cost_usd)}</span>
                    <span className="font-mono text-white/50">{r.tokens_in + r.tokens_out} tok</span>
                    <span className="font-mono text-white/50">{r.latency_ms_avg} ms</span>
                    {r.cache_hits > 0 && <span className="text-emerald-200">{r.cache_hits} reused</span>}
                    {r.errors > 0 && <span className="text-rose-200">{r.errors} failed</span>}
                </div>
            ))}
        </div>
    );
}

/** Network-wide AI consumption + cache stats + recent events (no prompts). */
export function NetworkUsagePanel({ usage, cache, isHub }) {
    const [events, setEvents] = useState(null);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!open) return;
        api.get("/admin/llm-network/events?limit=50").then((r) => setEvents(r.data.events)).catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)));
    }, [open]);
    const u = usage || {};
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6" data-testid="network-usage-panel">
            <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                <div className="font-display text-lg tracking-tight">Network AI consumption · last 24 h</div>
            </div>
            <p className="text-xs text-white/50 mb-4">Completions served by shared credentials, completions this environment ran for others, and results reused instead of re-generated.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 text-xs">
                <Stat label="served" value={u.served ?? 0} testId="usage-served" />
                <Stat label="reused" value={u.cache_hits ?? 0} sub={`${Math.round((u.cache_hit_rate || 0) * 100)} % hit rate`} testId="usage-reused" />
                <Stat label="spend" value={usd(u.cost_usd)} testId="usage-cost" />
                <Stat label="saved" value={usd(u.saved_usd)} testId="usage-saved" />
                <Stat label="failures" value={u.errors ?? 0} tone={u.errors ? "text-rose-200" : ""} testId="usage-errors" />
            </div>
            {isHub && cache && <div className="text-[11px] text-white/50 mb-3" data-testid="cache-stats">Shared result cache: <span className="font-mono text-white/80">{Number(cache.entries).toLocaleString()}</span> entries · <span className="font-mono text-white/80">{cache.hits}</span> hits · ≈ {usd(cache.saved_usd)} saved · TTL {cache.ttl_days} days</div>}
            <div className="grid gap-2 md:grid-cols-2">
                <Table title="By route" rows={u.by_route} testId="usage-by-route" />
                <Table title="By workload" rows={u.by_feature} testId="usage-by-feature" />
                <Table title="By requesting environment" rows={u.by_origin} testId="usage-by-origin" />
                <Table title="By credential owner" rows={u.by_owner} testId="usage-by-owner" />
            </div>
            <button onClick={() => setOpen((o) => !o)} className="mt-3 rounded border border-white/10 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5" data-testid="usage-events-toggle">{open ? "Hide" : "Show"} recent events</button>
            {open && (
                <ul className="mt-2 space-y-1 text-[11px]" data-testid="usage-events">
                    {events == null && <li className="text-white/40">Loading…</li>}
                    {events?.length === 0 && <li className="text-white/40" data-testid="usage-events-empty">No network events yet.</li>}
                    {events?.map((e) => (
                        <li key={e.id} className="flex flex-wrap gap-x-3 rounded border border-white/5 px-2 py-1" data-testid={`usage-event-${e.id}`}>
                            <span className="font-mono text-white/40">{new Date(e.timestamp).toLocaleString()}</span>
                            <span className={e.ok ? "text-emerald-200" : "text-rose-200"}>{e.route}</span>
                            <span className="text-white/70">{e.provider} · {e.model}</span>
                            {e.feature && <span className="font-mono text-white/50">{e.feature}</span>}
                            {e.origin_env && <span className="text-white/50">from {e.origin_env}</span>}
                            {e.owner_env && <span className="text-white/50">by {e.owner_env}</span>}
                            <span className="font-mono text-white/50">{e.latency_ms} ms · {usd(e.cost_usd)}</span>
                            {e.error && <span className="text-rose-200 break-all">{e.error}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Stat({ label, value, sub, tone = "", testId }) {
    return (
        <div className="rounded border border-white/10 bg-[#050505] px-3 py-2" data-testid={testId}>
            <div className="label-xs">{label}</div>
            <div className={`font-mono text-white/90 ${tone}`}>{value}</div>
            {sub && <div className="text-[10px] text-white/40">{sub}</div>}
        </div>
    );
}
